import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Button, Empty, Spin, Tabs, Tree, Typography, notification } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { CategoriesProxy } from '../services/catalogServiceAPI/categoriesProxy';
import { TYPE_LABELS, byPriorityThenTitle } from '../categories';
import { CategoryIcon } from './CategoryIcon';

const { Text } = Typography;

const HIDDEN = 'hidden';

/**
 * The tab a top-level category shows in. Clients list only top-level categories whose
 * type is `items` or `room`, so one with any other type (Holiday between seasons) is
 * shown to nobody; it gets its own tab here instead of passing for an Items category.
 */
const tabOf = (category) => (category.type === 'items' || category.type === 'room' ? category.type : HIDDEN);

function buildTreeData(categories, tab) {
    const byId = {};
    categories.forEach((c) => { byId[c.id] = { ...c, children: [] }; });
    categories.forEach((c) => {
        if (c.parentId && byId[c.parentId]) byId[c.parentId].children.push(byId[c.id]);
    });

    const toTreeNode = (node) => {
        const children = node.children.slice().sort(byPriorityThenTitle).map(toTreeNode);
        return {
            key: node.id,
            title: (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <CategoryIcon category={node} />
                    {node.title}
                </span>
            ),
            children: children.length ? children : undefined,
            isLeaf: children.length === 0,
        };
    };

    return categories
        .filter((c) => !c.parentId && tabOf(c) === tab)
        .map((c) => byId[c.id])
        .sort(byPriorityThenTitle)
        .map(toTreeNode);
}

/** The category tree, one tab per catalog tab plus the categories no client shows; a click opens the edit page. */
export function CategoryTree() {
    const history = useHistory();
    const proxy = useMemo(() => new CategoriesProxy(), []);
    const [categories, setCategories] = useState(null);

    useEffect(() => {
        proxy.list()
            .then(setCategories)
            .catch((e) => notification.error({ message: 'Failed to load categories', description: e.message }));
    }, [proxy]);

    const tree = (tab, empty) => {
        const treeData = buildTreeData(categories ?? [], tab);
        if (categories && treeData.length === 0) return <Empty description={empty} />;
        return (
            <Tree
                treeData={treeData}
                blockNode
                selectable
                selectedKeys={[]}
                onSelect={(_keys, { node }) => history.push(`/category/${node.key}/edit`)}
                style={{ background: 'transparent' }}
            />
        );
    };

    const tabItems = [
        { key: 'items', label: TYPE_LABELS.items, children: tree('items', 'No categories') },
        { key: 'room', label: TYPE_LABELS.room, children: tree('room', 'No categories') },
        {
            key: HIDDEN,
            label: 'Hidden categories',
            children: (
                <>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                        Top-level categories with no tab: neither the web nor the app shows them. Give one a tab on its
                        edit page, or schedule one there to open it for a season.
                    </Text>
                    {tree(HIDDEN, 'No hidden categories')}
                </>
            ),
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <Spin spinning={!categories}>
                <Tabs
                    items={tabItems}
                    tabBarExtraContent={<Link to="/category/new"><Button icon={<PlusOutlined />}>New category</Button></Link>}
                />
            </Spin>
        </div>
    );
}
